import { produce } from 'immer';
import { deepEqual } from '../datasourceUtils';
import { mapSilverPresetsToDataSource } from '../silver';
import { setBronzePresets } from '../bronze';
import { mapGoldPresetsToDataSource } from '../gold';
import { upgradePreTransformPreset } from '../preTransform';
import {
  getFieldsSpecDiffs,
  getFieldsSpecStatuses,
} from './fieldSpec/datasourceUpgradeFieldSpecHelper';
import {
  getFieldUtilsDiff,
  getFieldUtilsStatus,
} from './fieldUtils/datasourceUpgradeFieldUtilsHelper';
import { setPreTransformText } from './preTransform/datasourceUpgradePreTransformHelper';
import { setBronzeText } from './bronze/datasourceUpgradeBronzeHelper';
import {
  getSilverTableDiff,
  getSilverTableStatus,
  setSilverFilterChange,
  setSilverPostFilterChange,
  setSilverText,
} from './silver/datasourceUpgradeSilverHelper';
import {
  getGoldTableDiff,
  getGoldTableStatus,
  setGoldFilterChange,
  setGoldInputChange,
  setGoldPostFilterChange,
  setGoldText,
} from './gold/datasourceUpgradeGoldHelper';
import { getPresetChanges } from './preset/datasourceUpgradePresetHelper';
import {
  DatasourceUpgradeDiff,
  DatasourceUpgradeFieldStatus,
  DatasourceUpgradeItemDiff,
  DatasourceUpgradeStatus,
  DatasourceUpgradeTableDiff,
} from './datasourceUpgrade.types';
import {
  getAutoloaderDiff,
  getAutoloaderStatus,
} from './bronze/autoloader/datasourceUpgradeAutoloaderHelper';
import { Datasource_Preset, Datasource } from '@/types/api';
import { filterNulls } from '@/utils/filterNulls';

/**
 * Get the upgrade status of a field
 * @param currentPresetValue - The current version of the field
 * @param upgradePresetValue - The upgrade version of the field
 * @param value - The value of the field
 * @param status - The status of the field
 * @returns The status of the field
 */
export const getUpgradeFieldStatus = ({
  currentPresetValue,
  upgradePresetValue,
  currentValue,
}: {
  currentPresetValue: string | undefined | null | boolean | string[];
  upgradePresetValue: string | undefined | null | boolean | string[];
  currentValue: string | undefined | null | boolean | string[];
}): DatasourceUpgradeFieldStatus => {
  // is there no upgrade version or does the current version match the upgrade version?
  if (
    upgradePresetValue === undefined ||
    deepEqual(currentPresetValue, upgradePresetValue)
  ) {
    return 'unchanged';
  }

  // at this point we know there is upgrade version and it is different from the current version

  // if there is no value then we are just in an unapplied state
  if (!currentValue) {
    return 'unapplied';
  }

  // if the value already matches the upgrade then mark no change
  if (deepEqual(currentValue, upgradePresetValue)) {
    return 'unchanged';
  }

  // if the value already matches the current version then mark unapplied
  if (deepEqual(currentValue, currentPresetValue)) {
    return 'unapplied';
  }

  // otherwise we have a conflict
  return 'conflicted';
};

/**
 * Get the initial upgrade status of a datasource. This is used to construct the initial status of each field.
 * @param datasource - The datasource to get the initial upgrade status of
 * @param currentPresetValue - The current version of the preset
 * @param upgradePresetValue - The upgrade version of the preset
 * @returns The initial upgrade status of the datasource
 */
export const getInitialUpgradeStatus = (
  datasource: Datasource,
  currentPresetValue: Datasource_Preset,
  upgradePresetValue: Datasource_Preset,
): DatasourceUpgradeStatus => {
  const preTransformPresetName =
    datasource.spec.silver?.preTransform?.usePreset;

  const preTransformCurrentVersion =
    currentPresetValue.silver?.preTransform?.find(
      (t) => t.name === preTransformPresetName,
    );
  const preTransformUpgradeVersion =
    upgradePresetValue.silver?.preTransform?.find(
      (t) => t.name === preTransformPresetName,
    );

  const silverTransformNames = [
    ...new Set([
      ...(currentPresetValue.silver?.transform?.map((t) => t.name) || []),
      ...(upgradePresetValue.silver?.transform?.map((t) => t.name) || []),
    ]),
  ].filter(Boolean);

  const goldNames = [
    ...new Set([
      ...(currentPresetValue.gold?.map((t) => t.name) || []),
      ...(upgradePresetValue.gold?.map((t) => t.name) || []),
    ]),
  ].filter(Boolean);

  return {
    spec: {
      autoloader: getAutoloaderStatus(
        datasource,
        currentPresetValue,
        upgradePresetValue,
      ),
      silver: {
        preTransform: {
          name: datasource.spec.silver?.preTransform?.usePreset || '',
          filter: getUpgradeFieldStatus({
            currentPresetValue: preTransformCurrentVersion?.filter,
            upgradePresetValue: preTransformUpgradeVersion?.filter,
            currentValue: datasource.spec.silver?.preTransform?.filter,
          }),
          postFilter: getUpgradeFieldStatus({
            currentPresetValue: preTransformCurrentVersion?.postFilter,
            upgradePresetValue: preTransformUpgradeVersion?.postFilter,
            currentValue: datasource.spec.silver?.preTransform?.postFilter,
          }),
          fields: getFieldsSpecStatuses({
            customFields: filterNulls(
              datasource.spec.silver?.preTransform?.addFields || [],
            ),
            upgradeVersionFields: filterNulls(
              preTransformUpgradeVersion?.fields || [],
            ),
            currentVersionFields: filterNulls(
              preTransformCurrentVersion?.fields || [],
            ),
            omitFields:
              datasource.spec.silver?.preTransform?.presetOverrides
                ?.omitFields || [],
          }),
          utils: getFieldUtilsStatus({
            currentVersionUtils: preTransformCurrentVersion?.utils || undefined,
            upgradeVersionUtils: preTransformUpgradeVersion?.utils || undefined,
            customUtils:
              datasource.spec.silver?.preTransform?.utils || undefined,
          }),
        },
        transform: silverTransformNames.map((name) => {
          const upgradeTransform = upgradePresetValue?.silver?.transform?.find(
            (t) => t.name === name,
          );
          const currentTransform = currentPresetValue?.silver?.transform?.find(
            (t) => t.name === name,
          );
          const sourceTransform =
            datasource.spec.silver?.transform?.presetOverrides?.modifyTables?.find(
              (t) => t.name === name,
            );
          const transformStatus = {
            name: name || '',
            filter: getUpgradeFieldStatus({
              currentPresetValue: currentTransform?.filter,
              upgradePresetValue: upgradeTransform?.filter,
              currentValue: sourceTransform?.filter,
            }),
            postFilter: getUpgradeFieldStatus({
              currentPresetValue: currentTransform?.postFilter,
              upgradePresetValue: upgradeTransform?.postFilter,
              currentValue: sourceTransform?.postFilter,
            }),
            fields: getFieldsSpecStatuses({
              customFields: filterNulls(sourceTransform?.addFields || []),
              upgradeVersionFields: filterNulls(currentTransform?.fields || []),
              currentVersionFields: filterNulls(upgradeTransform?.fields || []),
              omitFields: sourceTransform?.omitFields || [],
            }),
            utils: getFieldUtilsStatus({
              currentVersionUtils: currentTransform?.utils || undefined,
              upgradeVersionUtils: upgradeTransform?.utils || undefined,
              customUtils: sourceTransform?.utils || undefined,
            }),
            status: getSilverTableStatus({
              datasource,
              upgradePresetValue,
              tableName: name || '',
            }),
          };
          return transformStatus;
        }),
      },
      gold: goldNames.map((name) => {
        const upgradeGold = upgradePresetValue?.gold?.find(
          (t) => t.name === name,
        );
        const currentGold = currentPresetValue?.gold?.find(
          (t) => t.name === name,
        );
        const sourceGold =
          datasource.spec.gold?.presetOverrides?.modifyTables?.find(
            (t) => t.name === name,
          );
        return {
          name,
          input: getUpgradeFieldStatus({
            currentPresetValue: currentGold?.input,
            upgradePresetValue: upgradeGold?.input,
            currentValue: currentGold?.input,
          }),
          filter: getUpgradeFieldStatus({
            currentPresetValue: currentGold?.filter,
            upgradePresetValue: upgradeGold?.filter,
            currentValue: sourceGold?.filter,
          }),
          postFilter: getUpgradeFieldStatus({
            currentPresetValue: currentGold?.postFilter,
            upgradePresetValue: upgradeGold?.postFilter,
            currentValue: sourceGold?.postFilter,
          }),
          fields: getFieldsSpecStatuses({
            customFields: filterNulls(sourceGold?.addFields || []),
            upgradeVersionFields: filterNulls(currentGold?.fields || []),
            currentVersionFields: filterNulls(upgradeGold?.fields || []),
            omitFields: sourceGold?.omitFields || [],
          }),
          status: getGoldTableStatus({
            datasource,
            upgradePresetValue,
            tableName: name,
          }),
        };
      }),
    },
  };
};

/**
 * Get the upgrade state of a datasource. This is used to construct the state of each field.
 * @param currentDatasource - The datasource to get the upgrade state of
 * @param currentPresetValue - The current version of the preset
 * @param upgradePresetValue - The upgrade version of the preset
 * @param status - The upgrade status of the datasource
 * @returns The upgrade state of the datasource
 */
export const getUpgradeDiff = (
  currentDatasource: Datasource,
  upgradeDatasource: Datasource,
  currentPresetValue: Datasource_Preset,
  upgradePresetValue: Datasource_Preset,
  status: DatasourceUpgradeStatus,
): DatasourceUpgradeDiff => {
  const preTransformPresetName =
    currentDatasource.spec.silver?.preTransform?.usePreset;

  const preTransformCurrentVersion =
    currentPresetValue?.silver?.preTransform?.find(
      (t) => t.name === preTransformPresetName,
    );
  const preTransformUpgradeVersion =
    upgradePresetValue?.silver?.preTransform?.find(
      (t) => t.name === preTransformPresetName,
    );
  const state: DatasourceUpgradeDiff = {
    spec: {
      autoloader: getAutoloaderDiff(
        currentDatasource,
        upgradeDatasource,
        currentPresetValue,
        upgradePresetValue,
        status,
      ),
      silver: {
        preTransform: {
          name: status.spec.silver.preTransform.name,
          filter: {
            currentPresetValue: preTransformCurrentVersion?.filter || undefined,
            upgradePresetValue: preTransformUpgradeVersion?.filter || undefined,
            currentValue:
              currentDatasource.spec.silver?.preTransform?.filter || undefined,
            upgradeValue:
              upgradeDatasource?.spec?.silver?.preTransform?.filter ||
              undefined,
            status: status.spec.silver.preTransform.filter,
            updateStatus: ({ draft, status: newStatus }) =>
              (draft.spec.silver.preTransform.filter = newStatus),
            type: 'string',
            setChanges: () => {}, // TODO: set this
            inputType: 'textarea',
          },
          postFilter: {
            currentPresetValue:
              preTransformCurrentVersion?.postFilter || undefined,
            upgradePresetValue:
              preTransformUpgradeVersion?.postFilter || undefined,
            currentValue:
              currentDatasource.spec.silver?.preTransform?.postFilter ||
              undefined,
            upgradeValue:
              upgradeDatasource?.spec?.silver?.preTransform?.postFilter ||
              undefined,
            status: status.spec.silver.preTransform.postFilter,
            updateStatus: ({ draft, status: newStatus }) =>
              (draft.spec.silver.preTransform.postFilter = newStatus),
            type: 'string',
            setChanges: () => {}, // TODO: set this
            inputType: 'textarea',
          },
          fields: getFieldsSpecDiffs({
            statuses: status.spec.silver.preTransform.fields,
            customFields: filterNulls(
              currentDatasource.spec.silver?.preTransform?.addFields || [],
            ),
            upgradeCustomFields: filterNulls(
              upgradeDatasource.spec.silver?.preTransform?.addFields || [],
            ),
            upgradeVersionFields: filterNulls(
              preTransformUpgradeVersion?.fields || [],
            ),
            currentVersionFields: filterNulls(
              preTransformCurrentVersion?.fields || [],
            ),
            stage: 'preTransform',
            upgradeOmitFields:
              currentDatasource.spec.silver?.preTransform?.presetOverrides
                ?.omitFields || [],
          }),
          utils: getFieldUtilsDiff({
            status: status.spec.silver.preTransform.utils,
            currentVersionUtils: preTransformCurrentVersion?.utils || undefined,
            upgradeVersionUtils: preTransformUpgradeVersion?.utils || undefined,
            currentUtils:
              currentDatasource.spec.silver?.preTransform?.utils || undefined,
            upgradeUtils:
              upgradeDatasource.spec.silver?.preTransform?.utils || undefined,
            stage: 'preTransform',
          }),
        },
        transform: status.spec.silver.transform.map(
          (transformStatus, index) => {
            const upgradeTransform =
              upgradePresetValue?.silver?.transform?.find(
                (t) => t.name === transformStatus.name,
              );
            const currentTransform =
              currentPresetValue?.silver?.transform?.find(
                (t) => t.name === transformStatus.name,
              );

            const currentSourceTransform =
              currentDatasource.spec.silver?.transform?.presetOverrides?.modifyTables?.find(
                (t) => t.name === currentTransform?.name,
              );
            const upgradeSourceTransform =
              upgradeDatasource.spec.silver?.transform?.presetOverrides?.modifyTables?.find(
                (t) => t.name === upgradeTransform?.name,
              );
            const transformState: DatasourceUpgradeDiff['spec']['silver']['transform'][number] =
              {
                name: transformStatus.name,
                filter: {
                  currentPresetValue: currentTransform?.filter || undefined,
                  upgradePresetValue: upgradeTransform?.filter || undefined,
                  currentValue: currentSourceTransform?.filter || undefined,
                  upgradeValue: upgradeSourceTransform?.filter || undefined,
                  status: transformStatus.filter,
                  updateStatus: ({ draft, status: newStatus }) =>
                    (draft.spec.silver.transform[index].filter = newStatus),
                  type: 'string',
                  setChanges: (params) => {
                    setSilverFilterChange({
                      tableName: transformStatus.name,
                      ...params,
                    });
                  },
                  inputType: 'textarea',
                },
                postFilter: {
                  currentPresetValue: currentTransform?.postFilter || undefined,
                  upgradePresetValue: upgradeTransform?.postFilter || undefined,
                  currentValue: currentSourceTransform?.postFilter || undefined,
                  upgradeValue: upgradeSourceTransform?.postFilter || undefined,
                  status: transformStatus.postFilter,
                  updateStatus: ({ draft, status: newStatus }) =>
                    (draft.spec.silver.transform[index].postFilter = newStatus),
                  type: 'string',
                  setChanges: (params) => {
                    setSilverPostFilterChange({
                      tableName: transformStatus.name,
                      ...params,
                    });
                  },
                  inputType: 'textarea',
                },
                fields: getFieldsSpecDiffs({
                  statuses: transformStatus.fields,
                  customFields: filterNulls(
                    currentSourceTransform?.addFields || [],
                  ),
                  upgradeCustomFields: filterNulls(
                    upgradeSourceTransform?.addFields || [],
                  ),
                  upgradeVersionFields: filterNulls(
                    upgradeTransform?.fields || [],
                  ),
                  currentVersionFields: filterNulls(
                    currentTransform?.fields || [],
                  ),
                  tableName: transformStatus.name,
                  stage: 'silver',
                  upgradeOmitFields: currentSourceTransform?.omitFields || [],
                }),
                utils: getFieldUtilsDiff({
                  status: transformStatus.utils,
                  currentVersionUtils: currentTransform?.utils || undefined,
                  upgradeVersionUtils: upgradeTransform?.utils || undefined,
                  currentUtils: currentSourceTransform?.utils || undefined,
                  upgradeUtils: upgradeSourceTransform?.utils || undefined,
                  stage: 'silver',
                  tableName: transformStatus.name,
                }),
                diff: getSilverTableDiff({
                  currentValue: currentSourceTransform,
                  upgradeValue: upgradeSourceTransform,
                  currentPresetValue: currentTransform,
                  upgradePresetValue: upgradeTransform,
                  status: transformStatus.status,
                  upgradedAddTable:
                    upgradeDatasource.spec.silver?.transform?.presetOverrides?.addTables?.find(
                      (t) => t.name === transformStatus.name,
                    ),
                  upgradeOmitTables:
                    upgradeDatasource.spec.silver?.transform?.presetOverrides
                      ?.omitTables || [],
                }),
              };
            return transformState;
          },
        ),
      },
      gold: status.spec.gold.map((goldStatus, index) => {
        const upgradeGold = upgradePresetValue?.gold?.find(
          (t) => t.name === goldStatus.name,
        );
        const currentGold = currentPresetValue?.gold?.find(
          (t) => t.name === goldStatus.name,
        );
        const currentSourceGold =
          currentDatasource.spec.gold?.presetOverrides?.modifyTables?.find(
            (t) => t.name === currentGold?.name,
          );
        const upgradeSourceGold =
          upgradeDatasource.spec.gold?.presetOverrides?.modifyTables?.find(
            (t) => t.name === upgradeGold?.name,
          );
        const goldState: DatasourceUpgradeDiff['spec']['gold'][number] = {
          name: goldStatus.name,
          input: {
            currentPresetValue: currentGold?.input || undefined,
            upgradePresetValue: upgradeGold?.input || undefined,
            currentValue: currentSourceGold?.sourceTable || undefined,
            upgradeValue: upgradeSourceGold?.sourceTable || undefined,
            status: goldStatus.input,
            updateStatus: ({ draft, status: newStatus }) =>
              (draft.spec.gold[index].input = newStatus),
            type: 'string',
            setChanges: (params) => {
              setGoldInputChange({
                tableName: goldStatus.name,
                value: params.value || '',
                datasource: params.datasource,
              });
            },
            inputType: 'fixed',
          },
          filter: {
            currentPresetValue: currentGold?.filter || undefined,
            upgradePresetValue: upgradeGold?.filter || undefined,
            currentValue: currentSourceGold?.filter || undefined,
            upgradeValue: upgradeSourceGold?.filter || undefined,
            status: goldStatus.filter,
            updateStatus: ({ draft, status: newStatus }) =>
              (draft.spec.gold[index].filter = newStatus),
            type: 'string',
            setChanges: (params) => {
              setGoldFilterChange({
                tableName: goldStatus.name,
                ...params,
              });
            },
            inputType: 'textarea',
          },
          postFilter: {
            currentPresetValue: currentGold?.postFilter || undefined,
            upgradePresetValue: upgradeGold?.postFilter || undefined,
            currentValue: currentSourceGold?.postFilter || undefined,
            upgradeValue: upgradeSourceGold?.postFilter || undefined,
            status: goldStatus.postFilter,
            updateStatus: ({ draft, status: newStatus }) =>
              (draft.spec.gold[index].postFilter = newStatus),
            type: 'string',
            setChanges: (params) => {
              setGoldPostFilterChange({
                tableName: goldStatus.name,
                ...params,
              });
            },
            inputType: 'textarea',
          },
          fields: getFieldsSpecDiffs({
            statuses: goldStatus.fields,
            customFields: filterNulls(currentSourceGold?.addFields || []),
            upgradeCustomFields: filterNulls(
              upgradeSourceGold?.addFields || [],
            ),
            upgradeVersionFields: filterNulls(currentGold?.fields || []),
            currentVersionFields: filterNulls(upgradeGold?.fields || []),
            tableName: goldStatus.name,
            stage: 'gold',
            upgradeOmitFields: currentSourceGold?.omitFields || [],
          }),
          diff: getGoldTableDiff({
            currentValue: currentSourceGold,
            upgradeValue: upgradeSourceGold,
            currentPresetValue: currentGold,
            upgradePresetValue: upgradeGold,
            status: goldStatus.status,
            upgradeOmitTables:
              upgradeDatasource.spec.gold?.presetOverrides?.omitTables || [],
            upgradedAddTable:
              upgradeDatasource.spec.gold?.presetOverrides?.addTables?.find(
                (t) => t.name === goldStatus.name,
              ),
          }),
        };
        return goldState;
      }),
    },
  };

  setBronzeText(state);
  setPreTransformText(state);
  setSilverText(state);
  setGoldText(state);

  return state;
};

/**
 * Get the status of a field
 * @param changes - The changes of the field
 * @returns The status of the field
 */
export const getDiffStatus = (
  changes: DatasourceUpgradeItemDiff[],
): DatasourceUpgradeFieldStatus => {
  let hasConflicts = false;
  let hasUnapplied = false;
  let hasConfirmed = false;
  changes.forEach((change) => {
    if (change.status === 'conflicted') {
      hasConflicts = true;
    }
    if (change.status === 'unapplied') {
      hasUnapplied = true;
    }
    if (change.status === 'confirmed') {
      hasConfirmed = true;
    }
  });

  if (hasConflicts) {
    return 'conflicted';
  }
  if (hasUnapplied) {
    return 'unapplied';
  }
  if (hasConfirmed) {
    return 'confirmed';
  }
  return 'unchanged';
};

/**
 * Get the status of a table
 * @param changes - The changes of the table
 * @returns The status of the table
 */
export const getTableDiffStatus = (
  changes: DatasourceUpgradeTableDiff[],
): DatasourceUpgradeFieldStatus => {
  const result = changes.map((change) => getDiffStatus(change.changes));
  if (result.includes('conflicted')) {
    return 'conflicted';
  }
  if (result.includes('unapplied')) {
    return 'unapplied';
  }
  if (result.includes('confirmed')) {
    return 'confirmed';
  }
  return 'unchanged';
};

/**
 * Apply the upgrade preset to the datasource. We first take the presets and construct a new preset with the changes.
 * Then we apply the new preset to the datasource.
 * @param datasource - The datasource to apply the upgrade preset to
 * @param currentPresetValue - The current version of the preset
 * @param upgradePresetValue - The upgrade version of the preset
 * @returns The datasource with the upgrade preset applied
 */
export const applyUpgradePreset = (
  datasource: Datasource,
  currentPresetValue: Datasource_Preset,
  upgradePresetValue: Datasource_Preset,
) => {
  const presetChanges = getPresetChanges(
    currentPresetValue,
    upgradePresetValue,
  );
  let newDatasource = setBronzePresets(datasource, presetChanges);
  newDatasource = upgradePreTransformPreset(newDatasource, presetChanges);
  newDatasource = mapSilverPresetsToDataSource(newDatasource, presetChanges);
  newDatasource = mapGoldPresetsToDataSource(newDatasource, presetChanges);
  newDatasource = produce(newDatasource, (draft) => {
    draft.spec.usePresetVersion = upgradePresetValue.versionNumber;
  });
  return newDatasource;
};
